import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { CancelTokenSource } from 'axios';
import { useCurrentUser } from 'hooks/useCurrentUser';
import {
  UserDetail,
  UserListMeta,
  UserListParams,
  UserListResponse,
} from './types';

interface UseUserDashboardUsersResult {
  users: UserDetail[];
  meta: UserListMeta | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useUserDashboardUsers = (params: UserListParams): UseUserDashboardUsersResult => {
  const user = useCurrentUser();
  const partner = user?.primaryPartner;
  const [users, setUsers] = useState<UserDetail[]>([]);
  const [meta, setMeta] = useState<UserListMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const cancelSourceRef = useRef<CancelTokenSource | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!partner?.id) return;

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    cancelSourceRef.current?.cancel('Superseded user dashboard request');
    const cancelSource = axios.CancelToken.source();
    cancelSourceRef.current = cancelSource;

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      queryParams.set('page', String(params.page));
      queryParams.set('per_page', String(params.per_page));
      queryParams.set('sort', params.sort);

      if (params.filter?.status) {
        queryParams.set('filter[status]', params.filter.status);
      }
      if (params.filter?.role) {
        queryParams.set('filter[role]', params.filter.role);
      }
      if (params.filter?.department_id) {
        queryParams.set('filter[department_id]', String(params.filter.department_id));
      }
      if (params.filter?.search) {
        queryParams.set('filter[search]', params.filter.search);
      }

      const response = await axios.get<UserListResponse>(
        `/api/v4/partners/${partner.id}/user_dashboard/users?${queryParams.toString()}`,
        { cancelToken: cancelSource.token }
      );

      if (requestId !== requestIdRef.current) {
        return;
      }

      const normalizedUsers: UserDetail[] = response.data.data.map((item) => ({
        id: item.id,
        ...item.attributes,
      }));

      setUsers(normalizedUsers);
      setMeta(response.data.meta);
    } catch (err: any) {
      if (axios.isCancel(err)) {
        return;
      }

      if (requestId !== requestIdRef.current) {
        return;
      }

      setUsers([]);
      setMeta(null);
      setError(err?.response?.data?.error || err?.message || 'Failed to load users');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [partner?.id, params.page, params.per_page, params.sort, params.filter?.status, params.filter?.role, params.filter?.department_id, params.filter?.search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    return () => {
      requestIdRef.current += 1;
      cancelSourceRef.current?.cancel('User dashboard request cleanup');
    };
  }, []);

  return { users, meta, loading, error, refetch: fetchUsers };
};
